/**
 * The one response in this product that is **bytes**, and therefore the one request the
 * dashboard makes outside tRPC.
 *
 * `apps/api/src/routes/artwork.ts` states the constraint from the other end: *"tRPC has no
 * way to carry bytes."* So `POST /v1/artwork` stays REST, this file is its client, and it
 * lives in `apps/app/src/api/` for the same reason `edge.ts` does — that directory is the
 * SPA's only door to the network, enforced by an eslint ban on `fetch(` everywhere else.
 *
 * **Unlike `edge.ts`, this request carries the org header and the session**, and both are
 * required rather than incidental: it is an authenticated write against one tenant's
 * catalogue, `requires("create", "artwork")` decides it, and a request without the header is
 * refused `ORG_NOT_SPECIFIED`. The header comes from `client.ts`'s `orgHeaders`, which is
 * still the only function that writes it.
 *
 * ## Everything this reads is a header, and that is the API's own projection
 *
 * The body is the file, so the route projects every advisory into `x-packagepage-*` headers
 * — codes only, never prose, because an HTTP header value is ISO-8859-1 and this product's
 * copy is full of em dashes. The prose the screen shows a person comes from
 * `artwork.preflight`, which is JSON. That division is the route's, not this file's, and it
 * is why the generator screen reads **two** sources: the preflight for what to say, and this
 * for what arrived.
 *
 * ## A refusal here is the same body a refusal anywhere else is
 *
 * On a non-2xx the response is an RFC 9457 document — the identical body `problemOf` lifts
 * out of tRPC's envelope on every other call. It is parsed with the shared schema rather
 * than cast, so a screen that narrows on `remedy` is narrowing on something checked.
 */
import {
  ARTWORK_WATERMARKS,
  artworkBatchRequestSchema,
  artworkBatchStatusSchema,
  artworkPreflightSchema,
  artworkRequestSchema,
  gateRefusalBodySchema,
  placementSheetRequestSchema,
  type ArtworkBatchRequest,
  type ArtworkBatchStatusView,
  type ArtworkRequest,
  type ArtworkWatermark,
  type GateRefusalBody,
  type PlacementSheetRequest,
  type ProblemBody,
} from "@packagepage/shared";

import { orgHeaders } from "./client.js";

/**
 * What the symbol measures, read back from the response that carries it.
 *
 * Millimetres **and** modules, because the two answer different questions and neither is
 * derivable from the other without the encoder this bundle may not contain: the millimetres
 * size the true-scale canvas, and the module counts are what a printer's prepress asks for.
 */
export interface ArtworkGeometry {
  readonly widthMm: number;
  readonly heightMm: number;
  readonly widthModules: number;
  readonly heightModules: number;
  readonly quietModules: number;
}

/** A generated file, in the form a browser can hold. */
export interface ArtworkResult {
  readonly ok: true;
  readonly blob: Blob;
  readonly filename: string;
  readonly mediaId: string;
  readonly digitalLinkUri: string;
  readonly watermarks: readonly ArtworkWatermark[];
  readonly geometry: ArtworkGeometry | null;
}

/** The server declined. The body is the sentence a person reads. */
export interface ArtworkRefused {
  readonly ok: false;
  readonly problem: ProblemBody | GateRefusalBody | null;
}

export type ArtworkOutcome = ArtworkResult | ArtworkRefused;

/**
 * `21.8x21.8` → the two numbers, or `null`.
 *
 * `null` rather than zeroes on an unparseable header, because a size of zero is a claim and
 * an absent size is the truth. The canvas renders its ruler note only when it has real
 * millimetres to render at — see `ArtworkPreview`'s docblock on why an invented scale is
 * worse than none.
 */
function pair(header: string | null): [number, number] | null {
  if (header === null) return null;
  const [a, b] = header.split("x");
  const width = Number(a);
  const height = Number(b);
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  return [width, height];
}

/**
 * The watermark codes the response says the file carries, held to the vocabulary.
 *
 * A code this build has never heard of is **dropped rather than passed through**, and the
 * asymmetry is deliberate: the screen renders an authored sentence per mark, so an unknown
 * code has no sentence and would render as a bare identifier. Dropping it loses a line;
 * passing it through would put a wire constant in front of a customer. The mark is on the
 * file either way — this is about what the screen can say about it.
 */
function watermarksOf(header: string | null): readonly ArtworkWatermark[] {
  if (header === null || header === "") return [];
  const known = new Set<string>(ARTWORK_WATERMARKS);
  return header.split(",").filter((code): code is ArtworkWatermark => known.has(code));
}

/** The RFC 9457 body behind a refusal, parsed with the gate schema where it is one. */
async function problemOfResponse(
  response: Response,
): Promise<ProblemBody | GateRefusalBody | null> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return null;
  }
  const gate = gateRefusalBodySchema.safeParse(body);
  if (gate.success) return gate.data;
  // Not every artwork refusal is a gate: §8.1 row 3 answers with the parser's own
  // `INVALID_CHECK_DIGIT`, which carries no remedy, and `NOT_FOUND` and the auth codes
  // reach here too. Those are ordinary problem bodies and the screen renders `detail`.
  if (typeof body !== "object" || body === null || !("code" in body)) return null;
  return body as ProblemBody;
}

/**
 * Ask for one file.
 *
 * The request is parsed with `artworkRequestSchema` **on the way out** — the same schema the
 * route validates with — so the defaults it applies (`applicationPreset`, `escapeHatch`) are
 * applied by one definition rather than by two that agree. `procedures.ts` gives every other
 * call the same treatment and the reason is the same: a screen that shipped its own default
 * would be sending a request it believes differs from the one the server received.
 */
export async function requestArtwork(request: ArtworkRequest): Promise<ArtworkOutcome> {
  const response = await fetch("/v1/artwork", {
    method: "POST",
    headers: { "content-type": "application/json", ...orgHeaders() },
    body: JSON.stringify(artworkRequestSchema.parse(request)),
  });

  if (!response.ok) {
    return { ok: false, problem: await problemOfResponse(response) };
  }

  const mm = pair(response.headers.get("x-packagepage-symbol-mm"));
  const modules = pair(response.headers.get("x-packagepage-symbol-modules"));
  const quiet = Number(response.headers.get("x-packagepage-quiet-modules"));

  return {
    ok: true,
    blob: await response.blob(),
    filename: filenameOf(response.headers.get("content-disposition"), request),
    mediaId: response.headers.get("x-packagepage-media-id") ?? "",
    digitalLinkUri: response.headers.get("x-packagepage-digital-link") ?? "",
    watermarks: watermarksOf(response.headers.get("x-packagepage-watermarks")),
    geometry:
      mm === null || modules === null || !Number.isFinite(quiet)
        ? null
        : {
            widthMm: mm[0],
            heightMm: mm[1],
            widthModules: modules[0],
            heightModules: modules[1],
            quietModules: quiet,
          },
  };
}

/** The placement sheet, in the form a browser can hold. */
export interface PlacementSheetResult {
  readonly ok: true;
  readonly blob: Blob;
  readonly filename: string;
  readonly mediaId: string;
  readonly digitalLinkUri: string;
}

export type PlacementSheetOutcome = PlacementSheetResult | ArtworkRefused;

/**
 * Ask for the dual-marking placement sheet — c96, `POST /v1/artwork/placement-sheet`.
 *
 * A **sibling** request rather than a second format on the one above, and the reason is not
 * on this side of the wire: §8.1 row 2's format scope is implemented as
 * `isPrintDeliverable(format)`, so a `"placement"` member of `ARTWORK_FORMATS` would be
 * classified a print deliverable and withheld from an unverified prefix — which is the one
 * outcome §8.2's fifth bullet forbids. `packages/shared`'s `placementSheetRequestSchema`
 * carries the whole argument; this file just has two `fetch`es instead of one.
 *
 * **No watermark header is read, because the response does not send one.** The sheet carries
 * no symbol, so §8.2 has nothing to mark, and a screen that read an absent header as an empty
 * set would be reporting a trust state it was never told. The generator screen says what it
 * knows — the sheet came — and says nothing about marks it cannot see.
 *
 * Parsed with `placementSheetRequestSchema` on the way out for `requestArtwork`'s reason: the
 * defaults (`applicationPreset`, `escapeHatch`) are applied by one definition rather than by
 * two that agree.
 */
export async function requestPlacementSheet(
  request: PlacementSheetRequest,
): Promise<PlacementSheetOutcome> {
  const response = await fetch("/v1/artwork/placement-sheet", {
    method: "POST",
    headers: { "content-type": "application/json", ...orgHeaders() },
    body: JSON.stringify(placementSheetRequestSchema.parse(request)),
  });

  if (!response.ok) {
    return { ok: false, problem: await problemOfResponse(response) };
  }

  const disposition = response.headers.get("content-disposition");
  const match = disposition === null ? null : /filename="([^"]+)"/.exec(disposition);

  return {
    ok: true,
    blob: await response.blob(),
    // The fallback names the product id and the symbology for `filenameOf`'s reason — there
    // is no GTIN in this bundle to build the server's own name from, so a second-best that
    // says so beats one pretending to be the same string.
    filename: match?.[1] ?? `${request.productId}-${request.symbology}-placement.pdf`,
    mediaId: response.headers.get("x-packagepage-media-id") ?? "",
    digitalLinkUri: response.headers.get("x-packagepage-digital-link") ?? "",
  };
}

/**
 * The name the file is saved under.
 *
 * Read from `Content-Disposition` where the server sent one — it is the server's name and
 * the storage key is derived from the same request — and reconstructed from the request only
 * as a fallback. The fallback is deliberately **not** the same expression the route uses: it
 * has no GTIN to work from here, so it names the product id, which is honest about being a
 * second-best rather than pretending to be the same string.
 */
export function filenameOf(
  disposition: string | null,
  request: Pick<ArtworkRequest, "productId" | "symbology" | "format">,
): string {
  const match = disposition === null ? null : /filename="([^"]+)"/.exec(disposition);
  return match?.[1] ?? `${request.productId}-${request.symbology}.${request.format}`;
}

/**
 * Start the batch ZIP, or learn which tier includes it — `POST /v1/artwork/batches` with
 * `zip` set (U62, §8.2's Starter line).
 *
 * REST rather than tRPC for the reason this file exists at the top: the batch's sibling
 * door answers bytes, and `check:approuter-census` holds that a mounted procedure is named
 * by a test. Both halves of one resource belong on one transport.
 *
 * Parsed with `artworkBatchRequestSchema` on the way out, like every other call here, so
 * `applicationPreset`'s default is applied by one definition rather than by two that agree.
 */
export interface BatchZipStarted {
  readonly ok: true;
  readonly batch: ArtworkBatchStatusView;
}

export type BatchZipOutcome = BatchZipStarted | ArtworkRefused;

export async function startBatchZip(
  request: ArtworkBatchRequest,
): Promise<BatchZipOutcome> {
  const response = await fetch("/v1/artwork/batches", {
    method: "POST",
    headers: { "content-type": "application/json", ...orgHeaders() },
    body: JSON.stringify(artworkBatchRequestSchema.parse({ ...request, zip: true })),
  });

  if (!response.ok) return { ok: false, problem: await problemOfResponse(response) };

  const parsed = artworkPreflightSchema.safeParse(await response.json());
  // A 200 whose `batch` is absent is the server answering the free preflight to a request
  // that asked for a ZIP, which would mean the two sides disagree about `zip`. Reported as
  // a refusal with no body rather than as a started job the screen would then poll forever.
  if (!parsed.success || parsed.data.batch === undefined) {
    return { ok: false, problem: null };
  }
  return { ok: true, batch: parsed.data.batch };
}

/** Where the job has got to — `GET /v1/artwork/batches/{id}`. */
export async function readBatchZip(id: string): Promise<BatchZipOutcome> {
  const response = await fetch(`/v1/artwork/batches/${encodeURIComponent(id)}`, {
    headers: orgHeaders(),
  });
  if (!response.ok) return { ok: false, problem: await problemOfResponse(response) };

  const parsed = artworkBatchStatusSchema.safeParse(await response.json());
  return parsed.success ? { ok: true, batch: parsed.data } : { ok: false, problem: null };
}
